/**
 * Software Design domain registration bootstrap.
 *
 * Registers all 11 domain profiles, the TestHealthProfile for journey
 * maps, the relationship pack, the journey field pack, and the 11
 * reference shape manifests. Call `registerSoftwareDesignDomain()` once
 * during app startup (same pattern as `registerNetworkDomain`).
 *
 * After scaffold, Epic B expands the shape list by adding manifests for
 * columns, methods, fragments, lifelines, ADR options, etc. — they're
 * appended to the shape registration loop below.
 *
 * See plans/software-design/foundation-scaffold.md § Layer 5.
 */

import { registerDomainProfile } from '../../../core/data-profiles';
import { fieldRegistry } from '../../../core/fields/registry';
import { registerRelationshipPack, registerShape } from '../../../lib/diagram/registries';
import type { DiagramType } from '../../../lib/diagram/registries';
import { registerShapeCategory } from '../../../lib/diagram/shapeCategoryRegistry';
import { Box, Database, Workflow, Activity, FileText, Layers, Component as ComponentIcon, Users } from 'lucide-react';

import { ErdProfile } from '../profiles/ErdProfile';
import { DatabaseProfile } from '../profiles/DatabaseProfile';
import { ClassProfile } from '../profiles/ClassProfile';
import { SequenceProfile } from '../profiles/SequenceProfile';
import { ComponentProfile } from '../profiles/ComponentProfile';
import { UseCaseProfile } from '../profiles/UseCaseProfile';
import { StateProfile } from '../profiles/StateProfile';
import { ActivityProfile } from '../profiles/ActivityProfile';
import { DfdProfile } from '../profiles/DfdProfile';
import { C4Profile } from '../profiles/C4Profile';
import { AdrProfile } from '../profiles/AdrProfile';
import { TestHealthProfile } from '../profiles/TestHealthProfile';

import { SoftwareDesignRelationshipPack } from '../connectors/relationships.pack';
import { JourneyFieldRegistryPack } from '../fields/journey.pack';
import { SoftwareDesignShapeManifests } from './manifests';

/** One-shot idempotent registration — safe to call multiple times. */
let registered = false;

export function registerSoftwareDesignDomain(): void {
  if (registered) return;
  registered = true;

  // 1. Domain profiles — one per sub-domain
  registerDomainProfile(ErdProfile);
  registerDomainProfile(DatabaseProfile);
  registerDomainProfile(ClassProfile);
  registerDomainProfile(SequenceProfile);
  registerDomainProfile(ComponentProfile);
  registerDomainProfile(UseCaseProfile);
  registerDomainProfile(StateProfile);
  registerDomainProfile(ActivityProfile);
  registerDomainProfile(DfdProfile);
  registerDomainProfile(C4Profile);
  registerDomainProfile(AdrProfile);

  // 2. Journey map profile — activates only when @view: journey
  registerDomainProfile(TestHealthProfile);

  // 3. Relationship pack
  registerRelationshipPack(SoftwareDesignRelationshipPack);

  // 4. Shape manifests — 11 reference shapes. Epic B adds the remaining ~54.
  // All shapes register under a single DiagramType slot for scaffold simplicity.
  // Epic B may split by domain when it replaces the generic renderers.
  const shapeDiagramType = 'uml' as DiagramType;
  for (const manifest of SoftwareDesignShapeManifests) {
    registerShape(shapeDiagramType, manifest);
  }
  const databaseShapeIds = new Set(['erd_entity', 'db_table']);
  for (const manifest of SoftwareDesignShapeManifests) {
    if (databaseShapeIds.has(manifest.id)) {
      registerShape('database' as DiagramType, manifest);
    }
  }

  // 5. Journey field pack
  fieldRegistry.registerPack(JourneyFieldRegistryPack);

  // 5. Shape-picker categories — self-register tabs for each of the 11
  //    software-design domains. See plans/software-design/playbook.md § X
  //    (App affordance wiring). Engineers extending Epic B can add more
  //    shape entries to these tabs as new manifests land.
  registerShapeCategory({
    id: 'software-design-erd',
    name: 'ERD',
    applicableDiagramTypes: ['uml', 'database'],
    order: 60,
    shapes: [{ id: 'erd_entity', label: 'Entity', icon: Database }],
  });
  registerShapeCategory({
    id: 'software-design-database',
    name: 'Database',
    applicableDiagramTypes: ['uml', 'database'],
    order: 61,
    shapes: [{ id: 'db_table', label: 'Table', icon: Database }],
  });
  registerShapeCategory({
    id: 'software-design-class',
    name: 'Class',
    applicableDiagramTypes: ['uml'],
    order: 62,
    shapes: [{ id: 'class_class', label: 'Class', icon: Box }],
  });
  registerShapeCategory({
    id: 'software-design-sequence',
    name: 'Sequence',
    applicableDiagramTypes: ['uml'],
    order: 63,
    shapes: [{ id: 'seq_participant', label: 'Participant', icon: Users }],
  });
  registerShapeCategory({
    id: 'software-design-component',
    name: 'Component',
    applicableDiagramTypes: ['uml'],
    order: 64,
    shapes: [{ id: 'comp_component', label: 'Component', icon: ComponentIcon }],
  });
  registerShapeCategory({
    id: 'software-design-usecase',
    name: 'Use Case',
    applicableDiagramTypes: ['uml'],
    order: 65,
    shapes: [{ id: 'uc_usecase', label: 'Use Case', icon: Box }],
  });
  registerShapeCategory({
    id: 'software-design-state',
    name: 'State',
    applicableDiagramTypes: ['uml'],
    order: 66,
    shapes: [{ id: 'state_state', label: 'State', icon: Box }],
  });
  registerShapeCategory({
    id: 'software-design-activity',
    name: 'Activity',
    applicableDiagramTypes: ['uml'],
    order: 67,
    shapes: [{ id: 'act_action', label: 'Action', icon: Activity }],
  });
  registerShapeCategory({
    id: 'software-design-dfd',
    name: 'DFD',
    applicableDiagramTypes: ['uml'],
    order: 68,
    shapes: [{ id: 'dfd_process', label: 'Process', icon: Workflow }],
  });
  registerShapeCategory({
    id: 'software-design-c4',
    name: 'C4',
    applicableDiagramTypes: ['uml'],
    order: 69,
    shapes: [{ id: 'c4_container', label: 'Container', icon: Layers }],
  });
  registerShapeCategory({
    id: 'software-design-adr',
    name: 'ADR',
    applicableDiagramTypes: ['uml'],
    order: 70,
    shapes: [{ id: 'adr_decision', label: 'Decision', icon: FileText }],
  });

  // 6. Agent tools register via registerSoftwareDesignAgentTools(registry)
  // from the assistant panel bootstrap, NOT from this file. Same pattern
  // as orgchart/network — tools are registered against a per-panel
  // registry, not a global registry.
}
